import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Plus, Edit, Trash2, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AnnouncementsSection = ({ isAdmin, token }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_active: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [isAdmin]);

  const fetchAnnouncements = async () => {
    try {
      const endpoint = isAdmin ? `${API}/admin/announcements` : `${API}/announcements`;
      const headers = isAdmin ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await axios.get(endpoint, { headers });
      setAnnouncements(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Gabim në ngarkimin e njoftimeve');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error('Ju lutem plotesoni të gjitha fushat');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/admin/announcements/${editingId}`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Njoftimi u përditesua me sukses');
      } else {
        await axios.post(`${API}/admin/announcements`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Njoftimi u shtua me sukses');
      }
      setDialogOpen(false);
      setFormData({ title: '', content: '', is_active: true });
      setEditingId(null);
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Gabim në ruajtjen e njoftimit');
    }
  };

  const handleEdit = (announcement) => {
    setEditingId(announcement.id);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      is_active: announcement.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt që dëshironi të fshini këtë njoftim?')) return;

    try {
      await axios.delete(`${API}/admin/announcements/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Njoftimi u fshi me sukses');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Gabim në fshirjen e njoftimit');
    }
  };

  return (
    <section id="njoftime" className="py-16 md:py-24 bg-background" data-testid="announcements-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-4">Njoftime & Aktivitete</h2>
            <p className="text-lg text-muted-foreground">
              Lajmet e fundit dhe aktivitetet e xhamisë
            </p>
          </motion.div>

          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingId(null);
                setFormData({ title: '', content: '', is_active: true });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent-700" data-testid="add-announcement-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Shto Njoftim
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="announcement-modal">
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Ndrysho Njoftimin' : 'Shto Njoftim të Ri'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Titulli</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Titulli i njoftimit"
                      data-testid="announcement-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">Përmbajtja</Label>
                    <Textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Përmbajtja e njoftimit"
                      rows={5}
                      data-testid="announcement-content-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="announcement-submit-button">
                    {editingId ? 'Përditëso' : 'Shto Njoftimin'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Duke ngarkuar njoftimet...</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl">
            <Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nuk ka njoftime për momentin</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {announcements.map((announcement, index) => (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card rounded-2xl shadow-md p-6 hover:shadow-xl transition-shadow"
                data-testid={`announcement-card-${announcement.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Bell className="w-5 h-5 text-accent" />
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="text-primary hover:text-primary-700 transition-colors"
                        data-testid={`edit-announcement-${announcement.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        data-testid={`delete-announcement-${announcement.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <h3 className="font-heading font-semibold text-xl mb-3 text-foreground">
                  {announcement.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                  {announcement.content}
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(announcement.date).toLocaleDateString('sq-AL')}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AnnouncementsSection;